import React, { useEffect, useState } from 'react';
import { Routes, Route, Link, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  runningPath,
  accountPath,
  storeAuthRedirect,
  storeReturnPathFromLocation,
} from '../utils/paths';
import AppSwitcher, { resolveActiveAppId } from './AppSwitcher';
import { SITE_NAME, SITE_APPS, formatAppTitle } from '../config/siteBranding';
import { setPageMeta, resetPageMeta } from '../utils/pageMeta';
import RunningLanding from './RunningLanding';
import RunningClubHome from './RunningClubHome';
import RunningRoutesPage from './RunningRoutesPage';
import RunningPracticeScheduling from './RunningPracticeScheduling';
import RunningPracticeDetail from './RunningPracticeDetail';
import PublicRunningPractice from './PublicRunningPractice';
import RunningClubsList from './RunningClubsList';
import RunningClubAdmin from './RunningClubAdmin';
import RunningProfile from './RunningProfile';
import RunningProfileRegistration from './RunningProfileRegistration';
import ClubRegistration from './ClubRegistration';
import MyClubs from './MyClubs';
import './MembershipStatusBanner.css';

const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  if (loading) return <div className="loading">Loading...</div>;
  if (!isAuthenticated) {
    storeAuthRedirect(window.location.pathname + window.location.search);
    return <Navigate to="/login" replace />;
  }
  return children;
};

const RunningApp = () => {
  const { isAuthenticated, user, logout, isAdmin, isClubCreator } = useAuth();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const location = useLocation();

  const closeMobileMenu = () => setMobileMenuOpen(false);
  const toggleMobileMenu = () => setMobileMenuOpen((open) => !open);

  const handleAuthNavClick = () => {
    storeReturnPathFromLocation(location.pathname, location.search);
    closeMobileMenu();
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        mobileMenuOpen &&
        !event.target.closest('.nav-menu') &&
        !event.target.closest('.mobile-menu-toggle')
      ) {
        closeMobileMenu();
      }
    };
    if (mobileMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = '';
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
      document.body.style.overflow = '';
    };
  }, [mobileMenuOpen]);

  const activeAppId = resolveActiveAppId(location.pathname);
  const isPublicShare = location.pathname.includes('/public/practices/');

  useEffect(() => {
    setPageMeta({
      title: formatAppTitle(SITE_APPS.running.name),
      description: SITE_APPS.running.description,
      url: `${window.location.origin}${SITE_APPS.running.href}`,
    });
    return () => resetPageMeta();
  }, [activeAppId]);

  return (
    <>
      {!isPublicShare && (
        <nav className="navbar">
          <div className="nav-container">
            <div className="nav-brand-block">
              <Link to="/" className="nav-logo" onClick={closeMobileMenu}>
                {SITE_NAME}
              </Link>
              <AppSwitcher
                activeAppId={activeAppId}
                onNavigate={closeMobileMenu}
                variant="compact"
                className="nav-app-switcher"
              />
            </div>
            <button
              className="mobile-menu-toggle"
              onClick={toggleMobileMenu}
              aria-label="Toggle menu"
            >
              <span></span>
              <span></span>
              <span></span>
            </button>
            <div className={`nav-menu ${mobileMenuOpen ? 'nav-menu-open' : ''}`}>
              <Link to={runningPath('/')} className="nav-link" onClick={closeMobileMenu}>
                Running home
              </Link>
              <Link to={runningPath('/clubs/list')} className="nav-link" onClick={closeMobileMenu}>
                Clubs
              </Link>
              {isAuthenticated && (
                <>
                  <Link to={accountPath()} className="nav-link" onClick={closeMobileMenu}>
                    Account
                  </Link>
                  <Link to={runningPath('/profile')} className="nav-link" onClick={closeMobileMenu}>
                    My profile
                  </Link>
                </>
              )}
              {isAuthenticated && (
                <Link to={runningPath('/clubs/mine')} className="nav-link" onClick={closeMobileMenu}>
                  My clubs
                </Link>
              )}
              {isAuthenticated && (isAdmin() || isClubCreator()) && (
                <Link
                  to={runningPath('/clubs/register')}
                  className="nav-link"
                  onClick={closeMobileMenu}
                >
                  Register club
                </Link>
              )}
              {isAuthenticated ? (
                <>
                  <span className="nav-link nav-user-info" style={{ color: 'var(--color-brand)' }}>
                    Hi, {user?.name?.split(' ')[0] || 'there'}
                  </span>
                  <button
                    type="button"
                    className="nav-link nav-logout-link"
                    onClick={() => {
                      logout();
                      closeMobileMenu();
                    }}
                  >
                    Log out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" className="nav-link" onClick={handleAuthNavClick}>
                    Log in
                  </Link>
                  <Link to="/register" className="nav-link" onClick={handleAuthNavClick}>
                    Join
                  </Link>
                </>
              )}
            </div>
          </div>
        </nav>
      )}

      <main className={`main-content${isPublicShare ? ' main-content--public' : ''}`}>
        <Routes>
          <Route path="/" element={<RunningLanding />} />
          <Route path="/clubs/list" element={<RunningClubsList />} />
          <Route
            path="/clubs/mine"
            element={
              <ProtectedRoute>
                <MyClubs sportFilter="RUNNING" basePath={runningPath()} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/clubs/register"
            element={
              <ProtectedRoute>
                <ClubRegistration defaultSport="RUNNING" successBasePath={runningPath()} />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile/complete"
            element={
              <ProtectedRoute>
                <RunningProfileRegistration />
              </ProtectedRoute>
            }
          />
          <Route
            path="/profile"
            element={
              <ProtectedRoute>
                <RunningProfile />
              </ProtectedRoute>
            }
          />
          <Route path="/clubs/:id" element={<RunningClubHome />} />
          <Route
            path="/clubs/:id/admin"
            element={
              <ProtectedRoute>
                <RunningClubAdmin />
              </ProtectedRoute>
            }
          />
          <Route path="/clubs/:id/routes" element={<RunningRoutesPage />} />
          <Route path="/clubs/:id/practices" element={<RunningPracticeScheduling />} />
          <Route path="/practices/:id" element={<RunningPracticeDetail />} />
          <Route path="/public/practices/:shareToken" element={<PublicRunningPractice />} />
          <Route path="*" element={<Navigate to={runningPath('/')} replace />} />
        </Routes>
      </main>
    </>
  );
};

export default RunningApp;
